import { ClockCircleOutlined, ScheduleOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { useEffect, useState } from "react";
import { addTurno, updateTurno } from "../../services/turnoService";

dayjs.extend(customParseFormat);

const TurnoForm = ({ onTurnoAdded, onTurnoUpdated, turnoToEdit }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [isEditing, setIsEditing] = useState(false);

	// Cargar datos del turno si estamos en modo edición
	useEffect(() => {
		if (turnoToEdit) {
			form.setFieldsValue({
				DescripcionTurno: turnoToEdit.DescripcionTurno,
				HorarioInicio: turnoToEdit.HorarioInicio
					? dayjs(turnoToEdit.HorarioInicio, "HH:mm")
					: null,
				HorarioFin: turnoToEdit.HorarioFin
					? dayjs(turnoToEdit.HorarioFin, "HH:mm")
					: null,
			});
			setIsEditing(true);
		} else {
			form.resetFields();
			setIsEditing(false);
		}
	}, [turnoToEdit, form]);

	const onFinish = async (values) => {
		try {
			// Limpiar error previo
			setError("");
			setLoading(true);

			// Formatear horarios
			const formattedValues = {
				DescripcionTurno: values.DescripcionTurno,
				HorarioInicio: values.HorarioInicio
					? values.HorarioInicio.format("HH:mm")
					: null,
				HorarioFin: values.HorarioFin
					? values.HorarioFin.format("HH:mm")
					: null,
			};

			if (isEditing) {
				// Asegurarse de preservar el No si ya existe
				if (turnoToEdit.No) {
					formattedValues.No = turnoToEdit.No;
				}

				// Actualizar turno existente
				const updatedTurno = await updateTurno(turnoToEdit.id, {
					...formattedValues,
					pendingSync: 1,
				});

				// Mostrar mensaje de éxito
				window.messageApi.success("Turno actualizado correctamente");

				// Notificar al componente padre
				if (onTurnoUpdated) onTurnoUpdated(updatedTurno);
			} else {
				// Guardar turno en IndexedDB
				const result = await addTurno(formattedValues);

				// Mostrar mensaje de éxito
				window.messageApi.success("Turno registrado correctamente");

				// Limpiar formulario
				form.resetFields();

				// Notificar al componente padre
				if (onTurnoAdded) onTurnoAdded(result);
			}
		} catch (error) {
			// Mostrar error específico
			setError(error.message || "Error al procesar el turno");
			console.error("Error al procesar turno:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setIsEditing(false);
		if (onTurnoUpdated) onTurnoUpdated(null);
	};

	return (
		<div className="turno-form-container">
			<h2>{isEditing ? "Editar Turno" : "Registro de Turno"}</h2>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="turno-form"
				onFinish={onFinish}
				layout="vertical"
				scrollToFirstError
			>
				<Form.Item
					name="DescripcionTurno"
					label="DescripcionTurno"
					rules={[
						{
							required: true,
							message: "Por favor ingrese la descripción del turno",
						},
					]}
				>
					<Input
						prefix={<ScheduleOutlined />}
						placeholder="Ingrese la descripción del turno"
					/>
				</Form.Item>

				<Form.Item
					name="HorarioInicio"
					label="Hora de Inicio"
					rules={[
						{
							required: true,
							message: "Por favor seleccione la hora de inicio",
						},
					]}
				>
					<TimePicker
						format="HH:mm"
						placeholder="Seleccione hora de inicio"
						style={{ width: "100%" }}
						prefix={<ClockCircleOutlined />}
					/>
				</Form.Item>

				<Form.Item name="HorarioFin" label="Hora de Fin">
					<TimePicker
						format="HH:mm"
						placeholder="Seleccione hora de fin (opcional)"
						style={{ width: "100%" }}
						prefix={<ClockCircleOutlined />}
					/>
				</Form.Item>

				<Form.Item>
					<div style={{ display: "flex", gap: "10px" }}>
						<Button type="primary" htmlType="submit" loading={loading} block>
							{isEditing ? "Actualizar Turno" : "Registrar Turno"}
						</Button>

						{isEditing && (
							<Button onClick={handleCancel} block>
								Cancelar
							</Button>
						)}
					</div>
				</Form.Item>
			</Form>
		</div>
	);
};

export default TurnoForm;
